import { useTranslation } from 'react-i18next';

import type { PlannedQuestion, QuestionGroup, QuickReply } from '@entities/search-session';

import { MAX_SELECTED_BY_SLOT, quickReplyText } from '../model/quickReply';

import styles from './Chat.module.css';

interface QuickRepliesProps {
  question: PlannedQuestion;
  disabled: boolean;
  /** Подсказки, чьи подписи сейчас стоят в черновике сообщения. */
  active: readonly string[];
  /** Вписать подсказку в черновик или убрать её оттуда. */
  onToggle: (group: QuestionGroup, reply: QuickReply) => void;
}

/**
 * Подсказки к текущему вопросу (MVP_PRD §9).
 *
 * Вопрос состоит из групп: «кузов и как ездите», «возраст и годовой пробег».
 * Где критерий — список (кузов, сценарии, топливо, приоритеты, оснащение), там
 * выбирается несколько значений сразу: один вопрос собирает больше, чем
 * цепочка отдельных.
 *
 * **Подсказка ничего не отправляет** (FE-595). Нажатие вписывает её подпись в
 * поле ввода, повторное — убирает; отправляет сообщение сам человек кнопкой
 * отправки, и до неё ответ можно дополнить своими словами. Отдельной кнопки
 * «Готово» нет: два способа отправить одно сообщение означали бы, что одно
 * из них отправляет не то, что написано в поле.
 *
 * Это подсказка, а не единственный путь: свободный текст принимается всегда,
 * и «неважно» есть среди вариантов там, где оно допустимо.
 */
export function QuickReplies({ question, disabled, active, onToggle }: QuickRepliesProps) {
  const { t, i18n } = useTranslation();

  const groups = question.groups;
  if (groups.length === 0) return null;

  const hasKey = (key: string) => i18n.exists(key);
  const label = (reply: QuickReply) => quickReplyText(reply, t, hasKey);
  const groupLabel = (group: QuestionGroup) =>
    hasKey(group.label_key) ? t(group.label_key) : t('chat.quickReplies.label');

  return (
    <div className={styles.quickRepliesBlock}>
      {question.repeat_index > 0 ? (
        <p className={styles.quickReplyHint} role="status">
          {t('chat.quickReplies.repeatHint')}
        </p>
      ) : null}

      {groups.map((group) => {
        const limit = MAX_SELECTED_BY_SLOT[group.slot];
        const chosen = group.quick_replies.filter((reply) => active.includes(reply.code)).length;
        return (
          <div key={group.slot} className={styles.quickReplyGroup}>
            {groups.length > 1 ? (
              <p className={styles.quickReplyGroupLabel}>
                {groupLabel(group)}
                {group.optional ? (
                  <span className={styles.quickReplyOptional}>
                    {t('chat.quickReplies.optional')}
                  </span>
                ) : null}
              </p>
            ) : null}
            <div className={styles.quickReplies} role="group" aria-label={groupLabel(group)}>
              {group.quick_replies.map((reply) => {
                const pressed = active.includes(reply.code);
                // Лимит приоритетов — три: молча отбрасывать четвёртый нельзя,
                // поэтому лишние кнопки просто перестают нажиматься.
                const capped =
                  limit !== undefined && !pressed && chosen >= limit && !reply.exclusive;
                return (
                  <button
                    key={reply.code}
                    type="button"
                    className={[styles.quickReply, pressed && styles.quickReplyActive]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={disabled || capped}
                    aria-pressed={pressed}
                    onClick={() => {
                      onToggle(group, reply);
                    }}
                  >
                    {label(reply)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      <p className={styles.quickReplyHint}>{t('chat.quickReplies.insertHint')}</p>
    </div>
  );
}
